import { z } from 'zod';

import { UuidSchema, BooleanQueryParamSchema } from './common.js';

/**
 * Geography lookups (read-only).
 *
 * Countries and states are seeded master data; these shapes back the
 * dropdowns in screens that reference geography (City Master, customer
 * addresses, branches). They are intentionally lightweight - id + the bits a
 * combobox needs - and are NOT paginated: the seeded set is small enough to
 * return whole and filter client-side.
 */

export const CountryOptionSchema = z.object({
  id: UuidSchema,
  /** ISO 3166-1 alpha-2, lowercased (e.g. "in", "us"). */
  isoCode: z.string(),
  name: z.string(),
});
export type CountryOption = z.infer<typeof CountryOptionSchema>;

export const CountryOptionListSchema = z.array(CountryOptionSchema);
export type CountryOptionList = z.infer<typeof CountryOptionListSchema>;

export const StateOptionSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  /** ISO 3166-2 subdivision code where available (e.g. "IN-MH"). */
  isoCode: z.string().nullable(),
});
export type StateOption = z.infer<typeof StateOptionSchema>;

export const StateOptionListSchema = z.array(StateOptionSchema);
export type StateOptionList = z.infer<typeof StateOptionListSchema>;

export const CountryOptionsQuerySchema = z.object({
  /** Case-insensitive contains match on name / iso code. */
  q: z.string().trim().min(1).max(120).optional(),
  /** Defaults to active-only; pass false to include deactivated rows. */
  activeOnly: BooleanQueryParamSchema.optional(),
});
export type CountryOptionsQuery = z.infer<typeof CountryOptionsQuerySchema>;

export const StateOptionsQuerySchema = z.object({
  countryId: UuidSchema,
  q: z.string().trim().min(1).max(120).optional(),
  activeOnly: BooleanQueryParamSchema.optional(),
});
export type StateOptionsQuery = z.infer<typeof StateOptionsQuerySchema>;

/**
 * In-use geography for a catalog list's Country / City filters.
 *
 * Every Inventory & Supply master (activities, transfers, hotels, meals, fixed
 * departures) exposes the same `/facets` shape: only the countries and cities
 * its own non-deleted rows actually reference, so the filter dropdowns show
 * real values rather than the whole geography master and grow as products for
 * new places are added.
 */
export const CatalogGeoFacetOptionSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  /**
   * On a city option: the country it sits in, so the City filter can narrow to
   * whatever the Country filter has selected. Null when the city carries no
   * resolved country FK; absent on country options.
   */
  countryId: UuidSchema.nullable().optional(),
});
export type CatalogGeoFacetOption = z.infer<typeof CatalogGeoFacetOptionSchema>;

export const CatalogGeoFacetsSchema = z.object({
  countries: z.array(CatalogGeoFacetOptionSchema),
  cities: z.array(CatalogGeoFacetOptionSchema),
});
export type CatalogGeoFacets = z.infer<typeof CatalogGeoFacetsSchema>;
